/**
 * Notification visibility — who may see which notification in the bell,
 * toast sync and history view.
 *
 * Pure logic (no React, no Firestore) so the rule is defined once and unit
 * tested directly. A notification is visible only when ALL of these hold:
 *   1. its type is known to the registry (old/removed types are hidden rather
 *      than crashing the lookup — history reaches much older docs than the bell)
 *   2. the user's notification settings opt into it (shouldShowInAppNotification)
 *   3. the user can currently access that notification page at its location
 *      (settings can outlive a revoked permission until the modal is re-saved)
 *
 * firestore.rules lets any signed-in user read the whole collection, so this is
 * a browser-side scoping rule, not a security boundary.
 */

import type { NotificationItem } from '@/types/notification.types';
import { NOTIFICATION_TYPE_REGISTRY, getPageIdFromNotificationType, isPersonalNotificationType } from '@/shared/config/notificationRegistry';
import { NOTIFICATION_PAGE_REGISTRY, NOTIFICATION_PAGE_IDS, type NotificationPageId } from '@/shared/config/notificationPageRegistry';

/**
 * Loose runtime shape of a user's notification settings. The stored document
 * has evolved across schema versions (v2 category-based, v3 page-based), so a
 * single user document may carry any subset of these fields. We read them
 * defensively rather than narrowing to one canonical type.
 */
interface RuntimePageSettings {
  inApp?: boolean;
  push?: boolean;
  locations?: string[];
  reminderEnabled?: boolean;
  newEventEnabled?: boolean;
  statusChangeEnabled?: boolean;
  statusTransitions?: {
    toScheduled?: boolean;
    toPickedUp?: boolean;
    toDelivered?: boolean;
  };
}

export interface RuntimeNotificationSettings {
  enabled?: boolean;
  // v3 page-based
  pages?: Record<string, RuntimePageSettings | undefined>;
  // legacy v2 category-based
  categories?: string[];
  categoryLocations?: Record<string, string[]>;
  locations?: string[];
}

/** The slice of CurrentUser the visibility rule reads. */
export interface NotificationViewer {
  email: string;
  role?: string;
  pagePermissions?: unknown;
  notificationSettings?: unknown;
}

/** Page permission entry as stored on the user (LocationBasedPageAccess, read loosely). */
interface RuntimePageAccess {
  enabled?: boolean;
  locations?: Record<string, { role?: string } | undefined>;
}

function getSettings(viewer: NotificationViewer): RuntimeNotificationSettings | undefined {
  return viewer.notificationSettings as RuntimeNotificationSettings | undefined;
}

function getPageAccess(viewer: NotificationViewer, permissionKey: string): RuntimePageAccess | undefined {
  const permissions = (viewer.pagePermissions || {}) as Record<string, RuntimePageAccess | undefined>;
  return permissions[permissionKey];
}

export function isKnownNotificationType(type: string): boolean {
  return Object.prototype.hasOwnProperty.call(NOTIFICATION_TYPE_REGISTRY, type);
}

/**
 * Check if a notification should be shown in-app based on user's page settings.
 * This filters notifications based on the v3 page-based notification settings.
 */
export function shouldShowInAppNotification(
  notification: NotificationItem,
  notificationSettings: RuntimeNotificationSettings | undefined,
  userEmail: string
): boolean {
  // Always exclude notifications triggered by the current user
  if (notification.metadata?.actionBy === userEmail) {
    return false;
  }

  // If no settings or settings disabled, don't show
  if (!notificationSettings?.enabled) {
    return false;
  }

  // Determine which page this notification belongs to
  const pageId = getPageIdFromNotificationType(notification.type);

  // Check v3 page-based settings
  const pages = notificationSettings.pages;
  if (pages) {
    const pageSettings = pages[pageId];
    if (!pageSettings) {
      return false; // Page not configured
    }
    if (!pageSettings.inApp) {
      return false; // In-app notifications disabled for this page
    }
    if (!pageSettings.locations?.includes(notification.locationId)) {
      return false; // Location not in user's list
    }

    // Additional filtering for trucking calendar notifications
    if (pageId === 'truckingCalendar') {
      const notificationType = notification.metadata?.notificationType || notification.type;

      // Filter "trucking_reminder" notifications
      if (notificationType === 'trucking_reminder') {
        // Default to true if setting not present (opt-out model)
        if (pageSettings.reminderEnabled === false) {
          return false;
        }
      }

      // Filter "trucking_event_scheduled" notifications
      if (notificationType === 'trucking_event_scheduled') {
        // Default to true if setting not present (opt-out model)
        if (pageSettings.newEventEnabled === false) {
          return false;
        }
      }

      // Filter "trucking_status_change" notifications
      if (notificationType === 'trucking_status_change') {
        // Default to true if setting not present (opt-out model)
        if (pageSettings.statusChangeEnabled === false) {
          return false;
        }

        // Check specific status transition settings
        const newStatus = notification.metadata?.newStatus;
        const transitions = pageSettings.statusTransitions || {};

        // Only filter if the setting is explicitly false (default is true)
        if (newStatus === 'scheduled' && transitions.toScheduled === false) {
          return false;
        }
        if (newStatus === 'picked_up' && transitions.toPickedUp === false) {
          return false;
        }
        if (newStatus === 'delivered' && transitions.toDelivered === false) {
          return false;
        }
      }
    }

    return true;
  }

  // Fallback to legacy v2 category-based settings
  const categories = notificationSettings.categories || [];
  const categoryLocations = notificationSettings.categoryLocations || {};
  const legacyLocations = notificationSettings.locations || [];

  // Map page to legacy categories (derived from registry)
  const pageToCategories = {} as Record<NotificationPageId, readonly string[]>;
  for (const id of NOTIFICATION_PAGE_IDS) {
    pageToCategories[id] = NOTIFICATION_PAGE_REGISTRY[id].legacyCategories;
  }

  const relevantCategories = pageToCategories[pageId] || [];

  for (const category of relevantCategories) {
    if (categories.includes(category)) {
      // Check category-specific locations
      if (categoryLocations[category]?.includes(notification.locationId)) {
        return true;
      }
      // Fallback to legacy global locations
      if (legacyLocations.includes(notification.locationId)) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Compute the union of all location IDs the user has subscribed to
 * for in-app notifications across all pages.
 */
export function getSubscribedLocations(notificationSettings: RuntimeNotificationSettings | undefined): string[] | null {
  if (!notificationSettings?.enabled || !notificationSettings.pages) return null;
  const locationSet = new Set<string>();
  for (const pageSettings of Object.values(notificationSettings.pages)) {
    if (pageSettings?.inApp && pageSettings.locations) {
      for (const loc of pageSettings.locations) locationSet.add(loc);
    }
  }
  return locationSet.size > 0 ? Array.from(locationSet).sort() : null;
}

/**
 * Whether the user can currently open a notification page at a location.
 * Mirrors usePagePermissions: admins see everything; everyone else needs the
 * page enabled with that location granted. The 'all' key grants every location
 * (same convention as getLocationIdsFromPageAccess in settingsLogic).
 */
export function canAccessNotificationPage(
  viewer: NotificationViewer,
  pageId: NotificationPageId,
  locationId: string
): boolean {
  if (viewer.role === 'admin') return true;
  const access = getPageAccess(viewer, NOTIFICATION_PAGE_REGISTRY[pageId].pagePermissionKey);
  if (!access?.enabled || !access.locations) return false;
  return Boolean(access.locations[locationId] || access.locations.all);
}

/** Can the user access at least one notification page at this location? */
function canAccessAnyNotificationPageAt(viewer: NotificationViewer, locationId: string): boolean {
  return NOTIFICATION_PAGE_IDS.some((pageId) => canAccessNotificationPage(viewer, pageId, locationId));
}

/** Personal (single-recipient) notification: sourced from userNotifications, or of a personal-only type. */
function isPersonalItem(notification: NotificationItem): boolean {
  return notification.source === 'personal' || isPersonalNotificationType(notification.type);
}

/**
 * Personal notifications are visible iff the type is registered and the viewer
 * is the recipient. They deliberately skip notification settings, page access,
 * location and targetRole checks. A broadcast doc of a personal type has no
 * recipient and so is never visible.
 */
function isPersonalNotificationVisible(notification: NotificationItem, viewer: NotificationViewer): boolean {
  if (!isKnownNotificationType(notification.type)) return false;
  if (!notification.recipient || !viewer.email) return false;
  return notification.recipient.toLowerCase() === viewer.email.toLowerCase();
}

/** The single visibility rule shared by the bell, toast sync and history view. */
export function isNotificationVisibleToUser(notification: NotificationItem, viewer: NotificationViewer): boolean {
  if (isPersonalItem(notification)) return isPersonalNotificationVisible(notification, viewer);
  if (!isKnownNotificationType(notification.type)) return false;
  if (!shouldShowInAppNotification(notification, getSettings(viewer), viewer.email)) return false;
  const pageId = getPageIdFromNotificationType(notification.type);
  return canAccessNotificationPage(viewer, pageId, notification.locationId);
}

/**
 * Locations to query notifications for: the user's subscribed locations
 * (or the selected location for users without v3 page settings), narrowed to
 * locations where they can access at least one notification page.
 * Returns null when there is nothing to query.
 */
export function getNotificationQueryLocations(
  viewer: NotificationViewer,
  fallbackLocation: string | null | undefined
): string[] | null {
  const candidates = getSubscribedLocations(getSettings(viewer)) ?? (fallbackLocation ? [fallbackLocation] : []);
  const accessible = candidates.filter((loc) => canAccessAnyNotificationPageAt(viewer, loc));
  return accessible.length > 0 ? accessible : null;
}
